import { Dispatch, SetStateAction, useEffect, useState } from "react";
import "leaflet-pixi-overlay"; // Must be called before the 'leaflet' import
import Drawer from "../../../../components/modules/map/drawer/Drawer";
import * as L from "leaflet";
import { ModeEnum } from "../../../../components/modules/map/MapContainer";
import { HashElement } from "../../../../context/map/units/unitsStore";
import Controls from "../../../../components/modules/map/controls/Controls";
import useContextMenu from "@pages/monitoring/map/hooks/useContextMenu";
import useShowOnMap from "@pages/monitoring/map/hooks/useShowOnMap";
import { useNavigate } from "react-router-dom";
import useTileLayers from "@pages/monitoring/map/hooks/useTileLayers";
import useGeoZoneRender from "@pages/monitoring/map/hooks/useGeoZoneRender";
import useUnitRender from "@pages/monitoring/map/hooks/useUnitRender";
import useMapMode from "@pages/monitoring/map/hooks/useMapMode";
import logger from "@utils/logger";
import { useTypedSelector } from "@hooks/useTypedSelector";
import useWorksLayers from "@pages/monitoring/map/hooks/useWorksLayers";
import useWeatherLayers from "@pages/monitoring/map/hooks/useWeatherLayers";
import useWMSLayers from "@pages/monitoring/map/hooks/useWMSLayers";
import * as PIXI from "pixi.js";
import {
    LeafletPixiApi,
    LeafletPixiApiSettings,
    PixiEntitySelection,
    RendererName
} from "@api/pixijs/renderers/OperationRenderer";
import { EntityId } from "@reduxjs/toolkit";
import useResetLayer from "@pages/monitoring/map/hooks/useResetLayer";
import { usePixiSettingsActions } from "@hooks/useActions";
import LeafletPixi, { PixiOverlayUtils } from "@pages/monitoring/map/hooks/LeafletPixi";
import { ResetLayerRenderer } from "@api/pixijs/renderers/ResetLayerRenderer";
import { UnitRenderer } from "@api/pixijs/renderers/UnitRenderer";
import { GeoZoneRenderer } from "@api/pixijs/renderers/GeoZoneRenderer";

export interface PixiState {
    showOnMap: boolean
    mapMode: ModeEnum
    selectedUnits: EntityId[]
    selectedGeoZones: EntityId[]
    // selected: SelectedElements
    contextMenuSelected?: HashElement
}

export type UseStateType<T> = [T, Dispatch<SetStateAction<T>>]
export type LeafletPixiContext = {
    service: LeafletPixiApi
    drawer: Drawer
    pixiLayer: L.Layer
    state: PixiState
    updateState: Dispatch<SetStateAction<PixiState>>
    map: L.Map
}

export type SelectedElements = { list: EntityId[], type: string }

const log = logger.getLogger("pixi.useLeafletPixi")

export default function () {
    let [pixiContext, setPixiContext] = useState<LeafletPixiContext>()
    const [state, setPixiState] = useState<PixiState>({
        // selected: { list: [], type: "" },
        selectedUnits: [],
        selectedGeoZones: [],
        mapMode: ModeEnum.BASE,
        showOnMap: false
    })
    const { data } = useTypedSelector(state => state.operations)
    const pixiSettings = useTypedSelector(state => state.pixiSettings)
    const { setPixiSettings } = usePixiSettingsActions()
    useEffect(() => {
        let map = L.map(`map-container`, {
            center: [53, 38],
            zoom: 10,
            minZoom: 2,
            maxZoom: 18,
            attributionControl: false,
            // @ts-ignore
            contextmenu: true,
            contextmenuWidth: 240,
            contextmenuItems: []
        });

        map.on("contextmenu", (e: any) => e.originalEvent.preventDefault());

        const controls = new Controls(map)
        const drawer = new Drawer(map);
        // const pixiOverlay = new LeafletPixiOverlayService(map);
        drawer.init({
            events: {
                create() {
                },
                save() {
                },
                update(e) {
                    log.info('editor update')
                }
            }
        })
        controls.init({
            polygonEditor: () =>
                setPixiState((state) => ({ ...state, mapMode: ModeEnum.POLYGON_EDITOR })),
            pointEditor: () =>
                setPixiState((state) => ({ ...state, mapMode: ModeEnum.MISSION_EDITOR })),
            clear: () => {
                setPixiState((state) => ({ ...state, mapMode: ModeEnum.BASE, showOnMap: true }))
            }
        });
        let firstDraw = true
        const overlayContainer = new PIXI.Container()
        overlayContainer.sortableChildren = true
        let leafletPixiApi: LeafletPixiApi | undefined
        if (pixiContext?.pixiLayer) pixiContext.pixiLayer.remove()
        // @ts-ignore
        const pixiLayer = L.pixiOverlay((utils: PixiOverlayUtils, events: any) => {
            const settings: LeafletPixiApiSettings = {
                zoom: utils.getMap().getZoom(),
                project: utils.latLngToLayerPoint,
                bounds: utils.getMap().getBounds(),
                scale: utils.getScale()
            }

            if (firstDraw) {
                log.debug("Install PIXI overlay...", settings)
                leafletPixiApi = LeafletPixi(utils, map, settings, data)

                leafletPixiApi.getRendererByName<ResetLayerRenderer>(RendererName.Reset).on('click', () => {
                    log.debug("Outside click...")
                    leafletPixiApi!.getRenderers().forEach(r => r.onOutsideClick())
                })
                leafletPixiApi.getRendererByName<UnitRenderer>(RendererName.Unit).on('select', (selected: PixiEntitySelection) =>
                    setPixiState((state) => ({ ...state, ...selected, showOnMap: true }))
                )

                leafletPixiApi.getRendererByName<GeoZoneRenderer>(RendererName.GeoZone).on('select', (selected: PixiEntitySelection) =>
                    setPixiState((state) => ({ ...state, ...selected, showOnMap: true }))
                )
                leafletPixiApi.getRendererByName<GeoZoneRenderer>(RendererName.GeoZone).on('geozoneedit', (selected: PixiEntitySelection) =>
                    setPixiState({
                        ...state,
                        ...selected,
                        showOnMap: true,
                        mapMode: ModeEnum.POLYGON_EDITOR
                    }))

                setPixiContext({
                    pixiLayer,
                    map,
                    drawer,
                    state,
                    updateState: setPixiState,
                    service: leafletPixiApi
                })
                firstDraw = false
            }
            setPixiSettings(settings)
            if (leafletPixiApi) {
                leafletPixiApi.getRenderers().forEach(i => i.onPixiOverlayChange(settings))
                leafletPixiApi.refreshPixiContainer()
            }
        }, overlayContainer, { padding: .2 })

        pixiLayer.addTo(map)
        return () => {
            map.remove()
        }
    }, [data])
    // useEffect(() => {
    //     if (pixiContext) {
    //
    //         pixiContext.service.refreshPixiContainer()
    //     }
    // }, [pixiContext, pixiSettings.zoom, pixiSettings.scale])
    // Навигация и зум на выбранный объект
    useShowOnMap(pixiContext, [state, setPixiState])
    // Отображение контекстного меню
    useContextMenu(pixiContext, [state, setPixiState])
    // Смена тайлов
    useTileLayers(pixiContext)
    // Отрисовка геозон
    useGeoZoneRender(pixiContext)
    // Отрисовка техники
    useUnitRender(pixiContext)
    // Смена режимов
    useMapMode(pixiContext, [state, setPixiState])
    // Слои работ
    useWorksLayers(pixiContext)
    // Слои погоды
    useWeatherLayers(pixiContext)
    // Информация росреестра
    useWMSLayers(pixiContext)

    useResetLayer(pixiContext)

    const navigate = useNavigate();
    useEffect(() => {
        if (state.showOnMap) {
            navigate(`units/${state.selectedUnits.join(":")}/details`, { replace: true });
        }
    }, [state.selectedUnits, state.mapMode]);
    useEffect(() => {
        if (state.showOnMap) {
            const isEditMode: any = state.mapMode === ModeEnum.POLYGON_EDITOR ? "edit" : "details";
            navigate(`fields/${state.selectedGeoZones.join(":")}/${isEditMode}`, { replace: true });
        }
    }, [state.selectedGeoZones, state.mapMode]);
    return pixiContext
}